import {
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
  logEvent as firebaseLogEvent,
} from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import {
  fetchAndActivate,
  getValue as firebaseGetValue,
  getRemoteConfig,
} from 'firebase/remote-config';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}appspot.com`,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const Firebase = initializeApp(firebaseConfig);

// Analytics (turned off due to changes in requirements)
// const analytics = Firebase && getAnalytics(Firebase);
const analytics = false;

export const setUserId = (id: string) =>
  analytics && firebaseSetUserId(analytics, id);
export const setUserProperties = (properties: {
  [key: string]: string | number | boolean | null | undefined;
}) => analytics && firebaseSetUserProperties(analytics, properties);
export const logEvent = (
  eventName: string,
  eventParams?: { [key: string]: string | number | boolean | null | undefined },
) => analytics && firebaseLogEvent(analytics, eventName, eventParams);

// Remote Config
const remoteConfig = Firebase && getRemoteConfig(Firebase);

if (remoteConfig && import.meta.env.VITE_ENV !== 'production') {
  remoteConfig.settings.minimumFetchIntervalMillis = 1000;
}

if (remoteConfig) {
  remoteConfig.defaultConfig = {
    VITE_FEATURE_FLAG_IS_AGENCY_MARKETING_KRAKEND_ENABLE: false,
    VITE_FEATURE_FLAG_SETTING_AGENT_EDIT_PHONE_NUMBER: false,
    VITE_FEATURE_FLAG_SETTING_AGENT_EDIT_EMAIL: false,
    VITE_FEATURE_FLAG_SET_ARE_TARGET_VALIDATION: false,
    VITE_FEATURE_FLAG_CREATE_WEEKLY_MEETING_MIDDLEWARE: false,
    VITE_FEATURE_FLAG_MAINTAIN_ASSIGNMENT_ARE_LIST_V2: false,
    VITE_FEATURE_FLAG_SELECT_MARKETING_ID: false,
    VITE_FEATURE_FLAG_GOAL_SETTINGS_V2: false,
    VITE_FEATURE_FLAG_ACTIVITY_DASHBOARD: false,
    VITE_FEATURE_FLAG_AGENT_MANAGEMENT_DETAIL_V2: false,
    VITE_FEATURE_FLAG_MAINTAIN_SELFIE_PHOTO: false,
    VITE_FEATURE_FLAG_SPV_MY_KPI_INCENTIVE: false,
    VITE_FEATURE_FLAG_AGENT_REACTIVATE_API_V2: false,
    VITE_FEATURE_FLAG_AGENT_REASSIGN_API_V2: false,
    VITE_FEATURE_FLAG_EDIT_AGENT_API_V2: false,
    VITE_FEATURE_FLAG_SPV_PERFORMANCE_COMPARISON: false,
    VITE_FEATURE_FLAG_MAINTAIN_REPORT_V2: false,
    VITE_FEATURE_FLAG_RECRUITMENT_LIST_V2: false,
    VITE_FEATURE_FLAG_RECRUITMENT_DOWNLOAD_V2: false,
    VITE_FEATURE_FLAG_BROCHURE_LIST_FOR_AM_BM: false,
    VITE_FEATURE_FLAG_CREATE_JOIN_VISIT_REPORT_V2: false,
    VITE_FEATURE_FLAG_BRANCH_ROUTINE_CHECKLIST: false,
    VITE_FEATURE_FLAG_SHOW_COMPANY_NAME: false,
    VITE_FEATURE_FLAG_SHOW_NOTIFICATION: false,
    VITE_FEATURE_FLAG_LEADS_TRACKER_BLACKLIST: false,
    VITE_FEATURE_FLAG_AGENT_DOCUMENT_UNSIGNED: false,
    VITE_FEATURE_FLAG_DYNAMIC_SPV_1ON1_SUGGESTIONS: false,
    VITE_FEATURE_FLAG_LEADS_TRACKER_DETAIL_V2: false,
    VITE_FEATURE_FLAG_AGENT_MANAGEMENT_FOR_PBF: false,
    VITE_FEATURE_FLAG_AGENT_MANAGEMENT_V2: false,
    VITE_FEATURE_FLAG_HIERARCHY_FOR_PBF: false,
    VITE_FEATURE_FLAG_KPI_INCENTIVE_FOR_PBF: false,
    VITE_FEATURE_FLAG_1ON1_FOR_PBF: false,
  };
}

let remoteConfigReady: Promise<boolean> | null = null;

export const ensureRemoteConfigReady = () => {
  remoteConfigReady =
    remoteConfigReady ??
    fetchAndActivate(remoteConfig)
      .then(() => true)
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Remote Config fetch failed:', err);
        return false;
      });

  return remoteConfigReady;
};

export const getValue = (key: string) => {
  if (!remoteConfig) return null;

  return firebaseGetValue(remoteConfig, key);
};

export default Firebase;
